import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import dict from "../i18n";
import api from "../api";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState(() => localStorage.getItem("tm_entity") || "piva_ind");
  const [lang, setLang] = useState(() => localStorage.getItem("tm_lang") || "it");
  const [theme, setTheme] = useState(() => localStorage.getItem("tm_theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("tm_theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("tm_lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("tm_entity", entity);
  }, [entity]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setAuthChecked(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      api.get("/entities").then((r) => setEntities(r.data)).catch(() => {});
    }
  }, [user]);

  const t = useCallback((key) => dict[lang]?.[key] ?? dict.it[key] ?? key, [lang]);

  const currentEntity = entities.find((e) => e.entity_id === entity) || null;

  const fmt = useCallback((amount, currency) => {
    const cur = currency || currentEntity?.currency || "EUR";
    try {
      return new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-GB", { style: "currency", currency: cur }).format(amount || 0);
    } catch {
      return `${cur} ${(amount || 0).toFixed(2)}`;
    }
  }, [lang, currentEntity]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  return (
    <AppContext.Provider value={{
      user, setUser, authChecked, entities, entity, setEntity, currentEntity,
      lang, setLang, theme, setTheme, t, fmt, logout, checkAuth,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
